import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Youtube, Instagram, Facebook, Twitter, Linkedin, Send } from 'lucide-react';
import { subscribeNewsletter } from '../api';
import { useSettings } from '../store';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const settings = useSettings();
  const phone = settings.phone || '+91-9650336344';
  const phoneTel = phone.replace(/\s+/g, '').startsWith('+') ? phone.replace(/\s+/g, '') : `+${phone.replace(/\D/g, '')}`;
  const contactEmail = settings.email || 'info@travelsera.com';
  const address = settings.address || '401, Spaze IT Park, Gurgaon, Haryana (India)';

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      try {
        await subscribeNewsletter(email);
      } catch (_) {}
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <footer className="bg-[#0a2a4a] text-gray-200">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <h4 className="text-white font-semibold text-sm tracking-wider mb-4">ABOUT COMPANY</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/page/about-us" className="hover:text-[#ffcc29] transition">About us</Link></li>
            <li><Link to="/page/terms-and-conditions" className="hover:text-[#ffcc29] transition">Terms & Condition</Link></li>
            <li><Link to="/page/privacy-policy" className="hover:text-[#ffcc29] transition">Privacy Policy</Link></li>
            <li><Link to="/page/contact-us" className="hover:text-[#ffcc29] transition">Contact Us</Link></li>
            <li><Link to="/page/careers" className="hover:text-[#ffcc29] transition">Careers</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm tracking-wider mb-4">QUICK LINKS</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/page/refund-policy" className="hover:text-[#ffcc29] transition">Refund Policy</Link></li>
            <li><Link to="/page/feedback" className="hover:text-[#ffcc29] transition">Feedback</Link></li>
            <li><Link to="/page/send-enquiry" className="hover:text-[#ffcc29] transition">Send Enquiry</Link></li>
            <li><Link to="/blog" className="hover:text-[#ffcc29] transition">Blog</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm tracking-wider mb-4">CONTACT US</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <MapPin size={16} className="text-[#ffcc29] mt-0.5 shrink-0" />
              <span>{address}</span>
            </li>
            <li className="flex items-center gap-2">
              <Phone size={16} className="text-[#ffcc29]" />
              <a href={`tel:${phoneTel}`} className="hover:text-[#ffcc29]">{phone}</a>
            </li>
            <li className="flex items-center gap-2">
              <Mail size={16} className="text-[#ffcc29]" />
              <a href={`mailto:${contactEmail}`} className="hover:text-[#ffcc29]">{contactEmail}</a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm tracking-wider mb-4">LET&apos;S STAY CONNECTED</h4>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2 mb-4 w-full min-w-0" data-testid="newsletter-form">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter Your Email Address"
              className="flex-1 min-w-0 w-full bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/60 rounded-md px-3 py-2 outline-none focus:border-[#ffcc29]"
              data-testid="newsletter-email-input"
            />
            <button type="submit" className="shrink-0 bg-[#ffcc29] hover:bg-[#e6b81f] text-[#0a2a4a] px-3 py-2 rounded-md text-sm font-semibold flex items-center justify-center gap-1 whitespace-nowrap" data-testid="newsletter-subscribe-btn">
              <Send size={14} /> Subscribe
            </button>
          </form>
          {subscribed && (
            <div className="text-xs text-[#ffcc29] mb-3">Thanks! You&apos;re subscribed.</div>
          )}
          <div>
            <div className="text-sm mb-2">Follow Us On :</div>
            <div className="flex gap-3 flex-wrap" data-testid="footer-socials">
              {[
                { url: settings.social_youtube, Ic: Youtube, key: 'youtube', label: 'YouTube' },
                { url: settings.social_instagram, Ic: Instagram, key: 'instagram', label: 'Instagram' },
                { url: settings.social_facebook, Ic: Facebook, key: 'facebook', label: 'Facebook' },
                { url: settings.social_twitter, Ic: Twitter, key: 'twitter', label: 'Twitter / X' },
                { url: settings.social_linkedin, Ic: Linkedin, key: 'linkedin', label: 'LinkedIn' },
              ].filter((s) => s.url && s.url.trim()).map(({ url, Ic, key, label }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                  data-testid={`social-${key}`}
                  className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-[#ffcc29] hover:text-[#0a2a4a] hover:border-[#ffcc29] transition"
                >
                  <Ic size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-4 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/70">
          <div>&copy; {new Date().getFullYear()} Travelsera. All rights reserved.</div>
          <div className="flex gap-4">
            <span>Visa</span>
            <span>Mastercard</span>
            <span>Amex</span>
            <span>Discover</span>
            <span>UPI</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
